import { supabase } from '@/lib/supabase'
import type { SocialPost, PostFilters } from '../types'

/**
 * Utility function to normalize post data
 */
export function normalizePost(post: any): SocialPost {
    return {
        ...post,
        image_urls: post.image_urls || [],
        hashtags: post.hashtags || [],
        mentions: post.mentions || [],
        tags: post.tags || [],
        processing_logs: post.processing_logs || []
    }
}

/**
 * Fetch all posts with optional filters
 */
export async function getPosts(filters?: PostFilters): Promise<SocialPost[]> {
    let query = supabase
        .from('social_posts')
        .select('*')
        .order('created_at', { ascending: false })

    if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status)
    }

    if (filters?.profiles && filters.profiles.length > 0) {
        query = query.in('profile_id', filters.profiles)
    }

    if (filters?.date_range) {
        query = query
            .gte('created_at', filters.date_range.start)
            .lte('created_at', filters.date_range.end)
    }

    if (filters?.search) {
        query = query.or(`caption.ilike.%${filters.search}%,content.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error

    return (data || []).map(normalizePost)
}

/**
 * Fetch a single post by ID
 */
export async function getPost(id: string): Promise<SocialPost> {
    const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .eq('id', id)
        .single()

    if (error) throw error

    return normalizePost(data)
}

/**
 * Create a new post
 */
export async function createPost(post: Partial<SocialPost>): Promise<SocialPost> {
    // Clean up UUID fields - convert empty strings to null
    const { id, created_at, updated_at, scheduled_time, ...postWithoutSystemFields } = post

    const normalizedScheduledTime =
        scheduled_time && scheduled_time !== '' ? scheduled_time : null

    const cleanedPost = {
        ...postWithoutSystemFields,
        profile_id: post.profile_id || null,
        queue_id: post.queue_id || null,
        scheduled_time: normalizedScheduledTime,
        hashtags: post.hashtags || [],
        image_urls: post.image_urls || [],
        mentions: post.mentions || [],
        tags: post.tags || []
    }

    const { data, error } = await supabase
        .from('social_posts')
        .insert(cleanedPost)
        .select()
        .single()

    if (error) throw error
    return data as SocialPost
}

/**
 * Update an existing post
 */
export async function updatePost(id: string, updates: Partial<SocialPost>): Promise<SocialPost> {
    const normalizedScheduledTime =
        updates.scheduled_time && updates.scheduled_time !== '' ? updates.scheduled_time : null

    const cleanedUpdates = {
        ...updates,
        profile_id: updates.profile_id || null,
        queue_id: updates.queue_id || null,
        scheduled_time: normalizedScheduledTime,
        updated_at: new Date().toISOString(),
        hashtags: updates.hashtags || [],
        image_urls: updates.image_urls || [],
        mentions: updates.mentions || [],
        tags: updates.tags || []
    }

    const { data, error } = await supabase
        .from('social_posts')
        .update(cleanedUpdates)
        .eq('id', id)
        .select()
        .single()

    if (error) throw error
    return data as SocialPost
}

/**
 * Delete a post
 */
export async function deletePost(id: string): Promise<void> {
    const { error } = await supabase
        .from('social_posts')
        .delete()
        .eq('id', id)

    if (error) throw error
}

/**
 * Fetch scheduled posts ready to be published
 */
export async function getScheduledPosts(): Promise<SocialPost[]> {
    const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .in('status', ['SCHEDULED', 'QUEUED'])
        .lte('scheduled_time', new Date().toISOString())
        .order('scheduled_time')

    if (error) throw error

    return (data || []).map(normalizePost)
}
